import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergeCreditNoteModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

const COL_CREDIT = "#0AB39C";
const COL_REDEEMED = "#F06547";

function formatAmt(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatSignedAmt(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  const abs = formatAmt(Math.abs(v));
  return v < 0 ? `-${abs}` : `+${abs}`;
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CreditNoteChartModel }} props
 */
function CreditNoteDonutBody({ model }) {
  const { t } = useTranslation();
  const credit = model?.donut?.creditPlus ?? 0;
  const redeemed = model?.donut?.redeemedMinus ?? 0;
  const total = credit + redeemed;
  const labelCredit = t("creditNoteSlicePlus", { defaultValue: "Credit Note (+)" });
  const labelRedeemed = t("creditNoteSliceMinus", { defaultValue: "Redeemed (-)" });

  const chartData = useMemo(
    () => ({
      labels: [labelCredit, labelRedeemed],
      datasets: [
        {
          data: [credit, redeemed],
          backgroundColor: [COL_CREDIT, COL_REDEEMED],
          borderColor: "#FFFFFF",
          borderWidth: 1,
        },
      ],
    }),
    [credit, redeemed, labelCredit, labelRedeemed],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "68%",
      rotation: -90,
      circumference: 360,
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "",
            label: (ctx) => {
              const i = ctx.dataIndex;
              const name = i === 0 ? labelCredit : labelRedeemed;
              const amt = i === 0 ? credit : redeemed;
              const pct = total > 0 ? ((amt / total) * 100).toFixed(1) : "0";
              return `${name} : ${formatAmt(amt)} (${pct}%)`;
            },
          },
        },
      },
    }),
    [credit, redeemed, labelCredit, labelRedeemed, total],
  );

  const centerTotal = model?.summary?.totalAmount ?? total;

  return (
    <div className="pos-credit-note-donut" aria-label={t("graphCreditNote", { defaultValue: "Credit Note" })}>
      <div className="pos-credit-note-donut__chart-wrap">
        <div className="pos-credit-note-donut__canvas">
          <Doughnut data={chartData} options={options} />
        </div>
        <div className="pos-credit-note-donut__center" aria-hidden>
          <span className="pos-credit-note-donut__center-label">
            {t("totalAmount", { defaultValue: "Total Amount" })}
          </span>
          <span className="pos-credit-note-donut__center-value">{formatAmt(centerTotal)}</span>
        </div>
      </div>
      <div className="pos-credit-note-donut__legend">
        <div className="pos-credit-note-donut__legend-item">
          <span className="pos-credit-note-donut__dot" style={{ background: COL_CREDIT }} aria-hidden />
          <span>{labelCredit}</span>
        </div>
        <div className="pos-credit-note-donut__legend-item">
          <span className="pos-credit-note-donut__dot" style={{ background: COL_REDEEMED }} aria-hidden />
          <span>{labelRedeemed}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CreditNoteChartModel; cardTitle: string }} props
 */
function CreditNoteBarBody({ model, cardTitle }) {
  const { t } = useTranslation();
  const summary = model?.summary ?? {};
  const dsCredit = t("creditNoteSlicePlus", { defaultValue: "Credit Note (+)" });
  const dsRedeemed = t("creditNoteSliceMinus", { defaultValue: "Redeemed (-)" });

  const yMax = useMemo(() => {
    const credit = model?.creditPlus ?? [];
    const redeemed = model?.redeemedMinus ?? [];
    const peak = Math.max(1, ...credit.map((x) => Number(x) || 0), ...redeemed.map((x) => Number(x) || 0));
    const cap = Math.max(peak * 1.15, 150000);
    return Math.ceil(cap / 50000) * 50000;
  }, [model?.creditPlus, model?.redeemedMinus]);

  const { chartData, options } = useMemo(() => {
    const labels = model?.categories ?? [];
    const credit = model?.creditPlus ?? [];
    const redeemed = model?.redeemedMinus ?? [];
    const data = {
      labels,
      datasets: [
        {
          label: dsCredit,
          data: credit.map((x) => Number(x) || 0),
          backgroundColor: COL_CREDIT,
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 36,
        },
        {
          label: dsRedeemed,
          data: redeemed.map((x) => Number(x) || 0),
          backgroundColor: COL_REDEEMED,
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 36,
        },
      ],
    };
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      layout: {
        padding: {
          top: 4,
          bottom: 6,
          left: 2,
          right: 4,
        },
      },
      datasets: {
        bar: {
          categoryPercentage: 0.72,
          barPercentage: 0.85,
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          align: "center",
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
            pointStyle: "circle",
            padding: 16,
            color: "#2A2A2A",
            font: { family: "'Segoe UI', Inter, sans-serif", size: 12, weight: "400" },
          },
        },
        tooltip: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          titleColor: "#434549",
          bodyColor: "#434549",
          borderColor: "#F2F2F2",
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          displayColors: true,
          titleFont: { family: "'Segoe UI', sans-serif", size: 12, weight: "600" },
          bodyFont: { family: "'Segoe UI', sans-serif", size: 12, weight: "400" },
          callbacks: {
            title: (items) => (items[0] ? String(items[0].label) : ""),
            label: (ctx) => {
              const v = ctx.parsed?.y ?? 0;
              return ` ${ctx.dataset.label}: ${formatAmt(v)}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            display: true,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            maxRotation: labels.length > 8 ? 45 : 0,
            minRotation: 0,
            autoSkip: false,
          },
          border: { display: false },
        },
        y: {
          min: 0,
          max: yMax,
          ticks: {
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => {
              const n = Number(v);
              if (!Number.isFinite(n)) return "";
              if (Math.abs(n) >= 1000) return `${Math.round(n / 1000)}k`;
              return String(n);
            },
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: { display: false },
        },
      },
    };
    return { chartData: data, options: opts };
  }, [model, dsCredit, dsRedeemed, yMax]);

  return (
    <div className="pos-credit-note-bar" aria-label={cardTitle}>
      <div className="pos-credit-note-bar__kpis">
        <div className="pos-credit-note-bar__kpi">
          <span className="pos-credit-note-bar__kpi-label">{t("totalAmount", { defaultValue: "Total Amount" })}</span>
          <span className="pos-credit-note-bar__kpi-value">{formatAmt(summary.totalAmount)}</span>
        </div>
        <div className="pos-credit-note-bar__kpi">
          <span className="pos-credit-note-bar__kpi-label">{dsCredit}</span>
          <span className="pos-credit-note-bar__kpi-value pos-credit-note-bar__kpi-value--credit">
            {formatAmt(summary.creditNotePlus)}
          </span>
        </div>
        <div className="pos-credit-note-bar__kpi">
          <span className="pos-credit-note-bar__kpi-label">{dsRedeemed}</span>
          <span className="pos-credit-note-bar__kpi-value pos-credit-note-bar__kpi-value--redeemed">
            {formatAmt(summary.redeemedMinus)}
          </span>
        </div>
        <div className="pos-credit-note-bar__kpi">
          <span className="pos-credit-note-bar__kpi-label">{t("Changes", { defaultValue: "Changes" })}</span>
          <span
            className={`pos-credit-note-bar__kpi-value ${
              (summary.changes ?? 0) < 0 ? "pos-credit-note-bar__kpi-value--redeemed" : "pos-credit-note-bar__kpi-value--credit"
            }`}
          >
            {formatSignedAmt(summary.changes ?? 0)}
          </span>
        </div>
      </div>
      <div className="pos-credit-note-bar__plot">
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").CreditNoteChartModel; rowIdPrefix: string }} props
 */
function CreditNoteTableBody({ model, rowIdPrefix }) {
  const { t } = useTranslation();
  const [sort, setSort] = useState(() => ({
    key: /** @type {"date"|"credit"|"redeemed"|"change"|"balance"} */ ("date"),
    dir: /** @type {"asc"|"desc"} */ ("asc"),
  }));

  useEffect(() => {
    setSort({ key: "date", dir: "asc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const headerCredit = t("creditNoteSlicePlus", { defaultValue: "Credit Note (+)" });
  const headerRedeemed = t("creditNoteSliceMinus", { defaultValue: "Redeemed (-)" });

  const sortedRows = useMemo(() => {
    const rows = (model?.tableRows ?? []).map((r, i) => ({
      ...r,
      _i: i,
      _key: `${rowIdPrefix}-${r.date}-${i}`,
    }));
    const dirM = sort.dir === "asc" ? 1 : -1;
    const parseDate = (s) => {
      const parts = String(s).split("/");
      if (parts.length >= 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]);
        const y = Number(parts[2]);
        if (Number.isFinite(d) && Number.isFinite(m) && Number.isFinite(y)) {
          const yr = y < 100 ? 2000 + y : y;
          return new Date(yr, m - 1, d).getTime();
        }
      }
      return NaN;
    };
    rows.sort((a, b) => {
      if (sort.key === "date") {
        const ta = parseDate(a.date);
        const tb = parseDate(b.date);
        if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta > tb ? dirM : -dirM;
        return a.date.localeCompare(b.date) * dirM;
      }
      if (sort.key === "credit") return (a.creditPlus - b.creditPlus) * dirM;
      if (sort.key === "redeemed") return (a.redeemedMinus - b.redeemedMinus) * dirM;
      if (sort.key === "change") return (a.change - b.change) * dirM;
      return (a.balance - b.balance) * dirM;
    });
    return rows;
  }, [model?.tableRows, sort, rowIdPrefix]);

  const foot = model?.footer;

  return (
    <div className="pos-credit-note-table" role="table" aria-label={t("graphCreditNote", { defaultValue: "Credit Note" })}>
      <div className="pos-credit-note-table__head" role="rowgroup">
        <div className="pos-credit-note-table__tr pos-credit-note-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName="pos-credit-note-table__th"
            sortedClassName="pos-credit-note-table__th--sorted"
            sorted={sort.key === "date"}
            direction={sort.dir}
            onSort={() => toggleSort("date")}
            ariaSortLabel={t("Sort by Date", { defaultValue: "Sort by Date" })}
            className="pos-credit-note-table__th--first"
          >
            {t("Date", { defaultValue: "Date" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-credit-note-table__th"
            sortedClassName="pos-credit-note-table__th--sorted"
            sorted={sort.key === "credit"}
            direction={sort.dir}
            onSort={() => toggleSort("credit")}
            ariaSortLabel={t("Sort by Credit Note", { defaultValue: "Sort by Credit Note" })}
            className="pos-credit-note-table__th--num"
          >
            {headerCredit}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-credit-note-table__th"
            sortedClassName="pos-credit-note-table__th--sorted"
            sorted={sort.key === "redeemed"}
            direction={sort.dir}
            onSort={() => toggleSort("redeemed")}
            ariaSortLabel={t("Sort by Redeemed", { defaultValue: "Sort by Redeemed" })}
            className="pos-credit-note-table__th--num"
          >
            {headerRedeemed}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-credit-note-table__th"
            sortedClassName="pos-credit-note-table__th--sorted"
            sorted={sort.key === "change"}
            direction={sort.dir}
            onSort={() => toggleSort("change")}
            ariaSortLabel={t("Sort by Changes", { defaultValue: "Sort by Changes" })}
            className="pos-credit-note-table__th--change"
          >
            {t("Changes", { defaultValue: "Changes" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName="pos-credit-note-table__th"
            sortedClassName="pos-credit-note-table__th--sorted"
            sorted={sort.key === "balance"}
            direction={sort.dir}
            onSort={() => toggleSort("balance")}
            ariaSortLabel={t("Sort by Balance", { defaultValue: "Sort by Balance" })}
            className="pos-credit-note-table__th--num pos-credit-note-table__th--last"
          >
            {t("Balance", { defaultValue: "Balance" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-credit-note-table__scroll" role="rowgroup">
        {sortedRows.map((row) => (
          <div key={row._key} className="pos-credit-note-table__tr" role="row">
            <div className="pos-credit-note-table__td pos-credit-note-table__td--date" role="cell">
              {row.date}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--credit" role="cell">
              {formatAmt(row.creditPlus)}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--redeemed" role="cell">
              {formatAmt(row.redeemedMinus)}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--change" role="cell">
              <span
                className={`pos-credit-note-table__pill ${
                  row.change < 0 ? "pos-credit-note-table__pill--down" : "pos-credit-note-table__pill--up"
                }`}
              >
                {formatSignedAmt(row.change)}
              </span>
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--balance" role="cell">
              {formatAmt(row.balance)}
            </div>
          </div>
        ))}
      </div>
      {foot ? (
        <div className="pos-credit-note-table__foot" role="rowgroup">
          <div className="pos-credit-note-table__tr pos-credit-note-table__tr--foot" role="row">
            <div className="pos-credit-note-table__td pos-credit-note-table__td--date pos-credit-note-table__td--total" role="cell">
              {foot.label}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--credit" role="cell">
              {formatAmt(foot.creditPlus)}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--redeemed" role="cell">
              {formatAmt(foot.redeemedMinus)}
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--change" role="cell">
              <span
                className={`pos-credit-note-table__pill ${
                  foot.change < 0 ? "pos-credit-note-table__pill--down" : "pos-credit-note-table__pill--up"
                }`}
              >
                {formatSignedAmt(foot.change)}
              </span>
            </div>
            <div className="pos-credit-note-table__td pos-credit-note-table__td--balance" role="cell">
              {formatAmt(foot.balance)}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").CreditNoteChartModel | null | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function CreditNoteChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {"donut"|"bar"|"table"} */ ("bar"));
  const merged = useMemo(() => mergeCreditNoteModel(model && typeof model === "object" ? model : null), [model]);
  const cardTitle = t("graphCreditNote", { defaultValue: "Credit Note" });

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("credit-note"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--credit-note${
        viewKind === "table" ? " pos-dashboard-chart-card--credit-note-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{cardTitle}</h2>
        <div className="pos-dashboard-chart-card__header-actions">
          <SegmentChartCardKebabMenu
            viewKind={viewKind}
            onViewKindChange={setViewKind}
            onDownload={handleDownloadPng}
            onRemove={onMenuRemove}
          />
        </div>
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--credit-note${
          viewKind === "table" ? " pos-dashboard-chart-card__body--credit-note-table" : ""
        }${viewKind === "bar" ? " pos-dashboard-chart-card__body--credit-note-bargraph" : ""}${
          viewKind === "donut" ? " pos-dashboard-chart-card__body--credit-note-donut" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--credit-note">
          {viewKind === "donut" ? <CreditNoteDonutBody model={merged} /> : null}
          {viewKind === "bar" ? <CreditNoteBarBody model={merged} cardTitle={cardTitle} /> : null}
          {viewKind === "table" ? <CreditNoteTableBody model={merged} rowIdPrefix="cn" /> : null}
        </div>
      </div>
    </div>
  );
}

export default CreditNoteChartCard;
